import React from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { Heart, ArrowRight } from "lucide-react";

interface MentorshipSectionProps {
  items: any[];
  isLoading: boolean;
}

export function MentorshipSection({ items, isLoading }: MentorshipSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border bg-neutral-50/50" aria-labelledby="mentorship-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Mentorship"
          icon={Heart}
          title="Mentorship"
          href="/mentorship"
        />
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-48 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item: any) => (
              <Card key={item.id} className="group hover:border-neutral-300 transition-all duration-150">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary font-heading font-bold text-lg">
                      {item.name?.charAt(0) ?? "?"}
                    </div>
                    <div>
                      <h4 className="text-sm font-heading font-semibold text-foreground">{item.name}</h4>
                      <p className="text-xs text-muted-foreground">{item.role ?? "Mentor"}</p>
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed line-clamp-2">
                    {item.bio ?? item.description}
                  </p>
                  <div className="mt-4">
                    <Button variant="ghost" size="sm" asChild className="p-0 h-auto text-xs gap-1">
                      <Link href={`/mentorship`}>
                        View profile <ArrowRight className="ml-1 h-3 w-3" />
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <EmptyState icon={Heart} description="No mentors available yet." />
        )}
      </div>
    </section>
  );
}